import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { collection, query, getDocs, doc, where, getDoc, setDoc } from "firebase/firestore";
import Modal from "./Modal";
import { LEARNING_CATEGORIES } from "../utils/categories";

const Learn = () => {
  const [user, loading, error] = useAuthState(auth);
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [ageGroup, setAgeGroup] = useState("adults");
  const [categoryCounts, setCategoryCounts] = useState({});

  useEffect(() => {
    console.log("User state:", { user, loading, error });
    if (loading) return; // Wait for auth to load
    if (error) console.error("Auth error:", error);
    if (user) {
      const fetchUserData = async () => {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const ageGroupFromDoc = userDoc.data().age_group || "adults";
            setAgeGroup(ageGroupFromDoc.toLowerCase());
          }
        } catch (err) {
          console.error("Fetch user data error:", err);
        }
      };
      fetchUserData();

      const fetchPosts = async () => {
        try {
          console.log("Fetching posts for user:", user.uid);
          const q = query(collection(db, "learn"));
          const querySnapshot = await getDocs(q);
          const postsData = await Promise.all(querySnapshot.docs.map(async (docSnap) => {
            const readDoc = await getDoc(doc(db, "learn", docSnap.id, "readStatus", user.uid));
            const read = readDoc.exists() ? readDoc.data().read : false;
            return {
              id: docSnap.id,
              ...docSnap.data(),
              read
            };
          }));
          setPosts(postsData);

          // Count articles per category
          const counts = {};
          postsData.forEach(post => {
            const category = post.category || 'uncategorized';
            counts[category] = (counts[category] || 0) + 1;
          });
          setCategoryCounts(counts);
        } catch (err) {
          console.error("Fetch posts error:", err);
        }
      };
      fetchPosts();
    }
  }, [user, loading, error]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
  };

  const handlePostClick = (post) => setSelectedPost(post);
  const handleCloseModal = () => setSelectedPost(null);
  const handleMarkRead = async (postId) => {
    if (user) {
      const post = posts.find(p => p.id === postId);
      const newRead = !post.read;
      const readRef = doc(db, "learn", postId, "readStatus", user.uid);

      try {
        await setDoc(readRef, { read: newRead });
        setPosts(posts.map(p => p.id === postId ? { ...p, read: newRead } : p));
        console.log("Update successful");
      } catch (err) {
        console.error("Mark read error:", err);
      }
    }
  };

  // Filter posts by selected category
  const filteredPosts = selectedCategory
    ? posts.filter(post => post.category === selectedCategory)
    : [];

  // Get selected category info
  const selectedCategoryInfo = LEARNING_CATEGORIES.find(cat => cat.id === selectedCategory);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!user) return <div>Please log in to view content.</div>;

  return (
    <div className="p-4 max-w-4xl mx-auto min-h-screen">
      <h1 className="text-3xl font-bold mb-6">Learn</h1>

      {!selectedCategory ? (
        // Category selection view
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {LEARNING_CATEGORIES.map(category => (
            <div
              key={category.id}
              className="p-6 bg-silver rounded-custom cursor-pointer hover:bg-blue-600 border border-gray-300 transition-colors"
              onClick={() => handleCategoryClick(category.id)}
            >
              <div className="text-center">
                <div className="text-4xl mb-3">{category.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{category.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{category.description}</p>
                <div className="text-blue-600 font-semibold">
                  {categoryCounts[category.id] || 0} articles
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Articles view for selected category
        <div>
          <div className="flex items-center mb-6">
            <button
              onClick={handleBackToCategories}
              className="mr-4 px-4 py-2 bg-gray-500 text-white rounded-custom hover:bg-gray-600 font-semibold"
            >
              ← Back to Categories
            </button>
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-custom border-2 border-blue-400 shadow-lg flex-1 text-white">
              <h2 className="text-3xl font-bold mb-2">
                {selectedCategoryInfo?.icon} {selectedCategoryInfo?.title}
              </h2>
              <p className="text-blue-100 text-lg">{selectedCategoryInfo?.description}</p>
            </div>
          </div>

          <div className="space-y-4">
            {filteredPosts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No articles found in this category yet.</p>
              </div>
            ) : (
              filteredPosts.map(post => (
                <div
                  key={post.id}
                  className="p-4 bg-silver rounded-custom cursor-pointer hover:bg-blue-600 border border-gray-300"
                  onClick={() => handlePostClick(post)}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-800">{post.title}</h3>
                    {post.read ? (
                      <span className="text-green-500 text-lg">✔</span>
                    ) : (
                      <span className="text-red-500 text-lg">●</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {selectedPost && (
        <Modal
          post={selectedPost}
          ageGroup={ageGroup}
          onClose={handleCloseModal}
          onMarkRead={() => handleMarkRead(selectedPost.id)}
        />
      )}
    </div>
  );
};

export default Learn;
